import { Box } from '@chakra-ui/react'
import { useDebouncedCallback } from 'use-debounce'
import LocationCard from './LocationCard'
import SearchBar from '../../../common/SearchBar'
import auctionFilter from '../../../../utilities/auctionFilter'
import { useEffect, useMemo, useState } from 'react'

export default function AuctionList({
  auctions,
  selectedId,
  onSelect,
  accent,
  excludedAuctionId,
  onSearch,
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  const [isSearching, setIsSearching] = useState(false)

  const debouncedSearch = useDebouncedCallback(async (term) => {
    if (!onSearch) return
    if (!term.trim()) {
      setSearchResults(null)
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    try {
      const results = await onSearch(term)
      setSearchResults(results || [])
    } finally {
      setIsSearching(false)
    }
  }, 400)

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [debouncedSearch])

  const handleSearchChange = (value) => {
    setSearchTerm(value)
    if (onSearch) {
      if (!value.trim()) {
        setSearchResults(null)
        setIsSearching(false)
        debouncedSearch.cancel()
      } else {
        setIsSearching(true)
        debouncedSearch(value)
      }
    }
  }

  const handleReset = () => {
    setSearchTerm('')
    setSearchResults(null)
    setIsSearching(false)
    debouncedSearch.cancel()
  }

  const displayedAuctions = useMemo(() => {
    const withoutExcluded = (list) => list.filter((auction) => auction.id !== excludedAuctionId)

    if (onSearch) {
      if (searchResults !== null) return withoutExcluded(searchResults)
      return withoutExcluded(auctions)
    }

    return auctionFilter(withoutExcluded(auctions), searchTerm)
  }, [auctions, excludedAuctionId, onSearch, searchResults, searchTerm])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SearchBar
        value={searchTerm}
        onChange={handleSearchChange}
        handleReset={handleReset}
        showSpinner={isSearching}
      />
      {displayedAuctions.map((auction) => (
        <LocationCard
          key={auction.id}
          loc={auction}
          accent={accent}
          isSelected={selectedId === auction.id}
          onClick={() => onSelect(auction)}
          match={searchTerm}
        />
      ))}
    </Box>
  )
}
